import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  MapPin, Camera, Check, X, AlertTriangle, 
  Loader2, Filter, Plus, Trash2, Upload, RefreshCw, Image
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHECKLIST_ITEMS = [
  { key: 'emergence', label: 'Emergence Issues', icon: '🌱' },
  { key: 'weeds', label: 'Weeds Present', icon: '🌿' },
  { key: 'pests', label: 'Pest Damage', icon: '🐛' },
  { key: 'disease', label: 'Disease Signs', icon: '🦠' },
  { key: 'nutrientDeficiency', label: 'Nutrient Deficiency', icon: '⚡' },
  { key: 'waterStress', label: 'Water Stress', icon: '💧' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: 'bg-amber-100 text-amber-800' },
  { value: 'checked', label: 'Checked', color: 'bg-blue-100 text-blue-800' },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-800' },
];

// BUG 1 FIX: Photo component with loading/error states
const PhotoThumbnail = ({ photo, onRemove, onClick }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  const imageUrl = photo.url || photo.preview || photo.storagePath;
  
  const handleRetry = (e) => {
    e.stopPropagation();
    setError(false);
    setLoading(true);
    setRetryCount(prev => prev + 1);
  };
  
  if (error) {
    return (
      <div className="relative w-20 h-20 bg-gray-100 rounded-lg flex flex-col items-center justify-center">
        <AlertTriangle className="w-5 h-5 text-danger mb-1" />
        <button onClick={handleRetry} className="text-xs text-primary hover:underline">
          Retry
        </button>
        {onRemove && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-danger text-white rounded-full flex items-center justify-center"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }
  
  return (
    <div className="relative w-20 h-20 cursor-pointer" onClick={onClick}>
      {loading && (
        <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      )}
      <img
        key={retryCount}
        src={imageUrl}
        alt="Scouting photo"
        className={`w-full h-full object-cover rounded-lg ${loading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setLoading(false)}
        onError={() => { setLoading(false); setError(true); }}
      />
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-1 -right-1 w-5 h-5 bg-danger text-white rounded-full flex items-center justify-center"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// BUG 1 FIX: Lightbox for full-size photos
const PhotoLightbox = ({ photos, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const photo = photos[currentIndex];
  const imageUrl = photo?.url || photo?.preview || photo?.storagePath;
  
  return (
    <div 
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100]"
      onClick={onClose}
    >
      <button 
        className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded"
        onClick={onClose}
      >
        <X className="w-8 h-8" />
      </button>
      
      {photos.length > 1 && (
        <>
          <button
            className="absolute left-4 text-white p-2 hover:bg-white/10 rounded"
            onClick={(e) => { e.stopPropagation(); setCurrentIndex(i => Math.max(0, i - 1)); }}
            disabled={currentIndex === 0}
          >
            ←
          </button>
          <button
            className="absolute right-4 text-white p-2 hover:bg-white/10 rounded"
            onClick={(e) => { e.stopPropagation(); setCurrentIndex(i => Math.min(photos.length - 1, i + 1)); }}
            disabled={currentIndex === photos.length - 1}
          >
            →
          </button>
        </>
      )}
      
      <img
        src={imageUrl}
        alt={`Photo ${currentIndex + 1}`}
        className="max-w-[90vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      <div className="absolute bottom-4 text-white text-sm">
        {currentIndex + 1} / {photos.length}
      </div>
    </div>
  );
};

const ScoutingPanel = ({ fieldId, farmId, onPointCreated, onOpenMap }) => {
  const { getAuthHeaders } = useAuth();
  const [scoutingPoints, setScoutingPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // New point form
  const [newPoint, setNewPoint] = useState({
    lat: '',
    lng: '',
    checklist: {
      emergence: false,
      weeds: false,
      pests: false,
      disease: false,
      nutrientDeficiency: false,
      waterStress: false,
    },
    notes: '',
    photos: [] // { file, preview } for new uploads
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadScoutingPoints();
  }, [fieldId]);

  const loadScoutingPoints = async () => {
    try {
      const response = await axios.get(
        `${API}/fields/${fieldId}/scouting-points`,
        { headers: getAuthHeaders() }
      );
      setScoutingPoints(response.data || []);
    } catch (error) {
      console.error('Failed to load scouting points:', error);
    } finally {
      setLoading(false);
    }
  };

  // BUG 1 FIX: Upload photos to server and get back URLs
  const uploadPhotosToServer = async (pointId, photos) => {
    const uploadedPhotos = [];
    
    for (const photo of photos) {
      if (!photo.file) continue; // Skip if no file (already uploaded)
      
      try {
        const formData = new FormData();
        formData.append('file', photo.file);
        formData.append('pointId', pointId);
        formData.append('fieldId', fieldId);
        
        const response = await axios.post(
          `${API}/scouting-points/${pointId}/photos`,
          formData,
          { 
            headers: { 
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        uploadedPhotos.push(response.data);
      } catch (err) {
        console.error('Failed to upload photo:', err);
        toast.error('Failed to upload one or more photos');
      }
    }
    
    return uploadedPhotos;
  };

  const handleCreatePoint = async (lat, lng) => {
    const pointData = {
      lat: lat || parseFloat(newPoint.lat),
      lng: lng || parseFloat(newPoint.lng),
      fieldId,
      farmId,
      status: 'open',
      checklist: newPoint.checklist,
      notes: newPoint.notes,
      photos: [] // Photos will be uploaded after point is created
    };

    if (!pointData.lat || !pointData.lng) {
      toast.error('Please enter valid coordinates');
      return;
    }

    try {
      setUploading(true);
      
      // 1. Create the scouting point first
      const response = await axios.post(
        `${API}/fields/${fieldId}/scouting-points`,
        pointData,
        { headers: getAuthHeaders() }
      );
      
      const createdPoint = response.data;
      
      // 2. Upload photos if any (BUG 1 FIX)
      if (newPoint.photos.length > 0) {
        const uploadedPhotos = await uploadPhotosToServer(createdPoint.id, newPoint.photos);
        createdPoint.photos = uploadedPhotos;
      }
      
      toast.success('Scouting point created');
      setScoutingPoints(prev => [createdPoint, ...prev]);
      setShowAddModal(false);
      resetNewPoint();
      
      if (onPointCreated) onPointCreated(createdPoint);
    } catch (error) {
      console.error('Create point error:', error);
      toast.error('Failed to create scouting point');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateStatus = async (pointId, newStatus) => {
    try {
      await axios.patch(
        `${API}/scouting-points/${pointId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      
      setScoutingPoints(prev => 
        prev.map(p => p.id === pointId ? { ...p, status: newStatus } : p)
      );
      
      if (selectedPoint?.id === pointId) {
        setSelectedPoint(prev => ({ ...prev, status: newStatus }));
      }
      
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeletePoint = async (pointId) => {
    if (!window.confirm('Delete this scouting point?')) return;
    
    try {
      await axios.delete(
        `${API}/scouting-points/${pointId}`,
        { headers: getAuthHeaders() }
      );
      
      setScoutingPoints(prev => prev.filter(p => p.id !== pointId));
      setSelectedPoint(null);
      toast.success('Scouting point deleted');
    } catch (error) {
      toast.error('Failed to delete scouting point');
    }
  };

  // BUG 1 FIX: Handle photo upload for existing point
  const handleAddPhotoToExistingPoint = async (e, pointId) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('pointId', pointId);
        formData.append('fieldId', fieldId);
        
        const response = await axios.post(
          `${API}/scouting-points/${pointId}/photos`,
          formData,
          { 
            headers: { 
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        // Update the point in state
        setScoutingPoints(prev => 
          prev.map(p => p.id === pointId 
            ? { ...p, photos: [...(p.photos || []), response.data] }
            : p
          )
        );
        
        if (selectedPoint?.id === pointId) {
          setSelectedPoint(prev => ({
            ...prev,
            photos: [...(prev.photos || []), response.data]
          }));
        }
        
        toast.success('Photo uploaded');
      } catch (err) {
        console.error('Failed to upload photo:', err);
        toast.error('Failed to upload photo');
      }
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newPhotos = [];
    for (const file of files) {
      const preview = URL.createObjectURL(file);
      newPhotos.push({ file, preview });
    }

    setNewPoint(prev => ({
      ...prev,
      photos: [...prev.photos, ...newPhotos]
    }));
  };

  const removePhoto = (index) => {
    setNewPoint(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const resetNewPoint = () => {
    // Revoke object URLs to free memory
    newPoint.photos.forEach(p => {
      if (p.preview) URL.revokeObjectURL(p.preview);
    });
    
    setNewPoint({
      lat: '',
      lng: '',
      checklist: {
        emergence: false,
        weeds: false,
        pests: false,
        disease: false,
        nutrientDeficiency: false,
        waterStress: false,
      },
      notes: '',
      photos: []
    });
  };

  const filteredPoints = scoutingPoints.filter(p => 
    filter === 'all' || p.status === filter
  );

  const getStatusBadge = (status) => {
    const colors = {
      'open': { bg: 'rgba(245,158,11,0.15)', color: 'var(--warning)', border: 'rgba(245,158,11,0.3)' },
      'checked': { bg: 'rgba(59,130,246,0.15)', color: 'var(--info)', border: 'rgba(59,130,246,0.3)' },
      'resolved': { bg: 'rgba(34,197,94,0.15)', color: 'var(--success)', border: 'rgba(34,197,94,0.3)' }
    };
    const style = colors[status] || colors['open'];
    const option = STATUS_OPTIONS.find(o => o.value === status);
    return option ? (
      <span 
        className="px-2 py-1 text-xs rounded-full"
        style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}` }}
      >
        {option.label}
      </span>
    ) : null;
  };

  const getChecklistSummary = (checklist) => {
    if (!checklist) return null;
    const issues = CHECKLIST_ITEMS.filter(item => checklist[item.key]);
    if (issues.length === 0) return <span className="text-green-600 text-sm">No issues</span>;
    return (
      <div className="flex gap-1 flex-wrap">
        {issues.map(item => (
          <span key={item.key} title={item.label} className="text-lg">
            {item.icon}
          </span>
        ))}
      </div>
    );
  };

  const openLightbox = (photos, index) => {
    setLightboxPhotos(photos);
    setLightboxIndex(index);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="scouting-panel">
      {/* Header with filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input-depth text-sm"
            style={{ width: 'auto', padding: '6px 12px' }}
          >
            <option value="all">All ({scoutingPoints.length})</option>
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label} ({scoutingPoints.filter(p => p.status === opt.value).length})
              </option>
            ))}
          </select>
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-depth-primary text-sm flex items-center gap-1"
          data-testid="add-scouting-point-btn"
        >
          <Plus className="w-4 h-4" />
          Add Point
        </button>
      </div>

      {/* BUG 2 FIX (Option A): Clarify that scouting is separate from tasks */}
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Scouting points track field observations. To create tasks, use the Tasks tab or alerts.
      </p>

      {/* Scouting points list */}
      {filteredPoints.length === 0 ? (
        <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
          <MapPin className="w-12 h-12 mx-auto mb-2" style={{ opacity: 0.5 }} />
          <p>No scouting points yet</p>
          <p className="text-sm">Use Scout mode on the NDVI map or click "Add Point"</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPoints.map(point => (
            <div
              key={point.id}
              className="p-4 cursor-pointer transition rounded-xl"
              style={{
                background: point._pending ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${point._pending ? 'rgba(245,158,11,0.3)' : 'var(--border-soft)'}`
              }}
              onClick={() => setSelectedPoint(point)}
              data-testid={`scouting-point-${point.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusBadge(point.status)}
                    {point._pending && (
                      <span className="text-xs" style={{ color: 'var(--warning)' }}>Pending sync</span>
                    )}
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {new Date(point.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {point.lat?.toFixed(5)}, {point.lng?.toFixed(5)}
                    </span>
                    {point.photos?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Camera className="w-3 h-3" />
                        {point.photos.length}
                      </span>
                    )}
                  </div>
                  
                  {getChecklistSummary(point.checklist)}
                  
                  {point.notes && (
                    <p className="text-sm mt-2 line-clamp-2" style={{ color: 'var(--text)' }}>
                      {point.notes}
                    </p>
                  )}
                  
                  {/* BUG 1 FIX: Show photo thumbnails in list */}
                  {point.photos?.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {point.photos.slice(0, 3).map((photo, idx) => (
                        <PhotoThumbnail 
                          key={idx} 
                          photo={photo}
                          onClick={(e) => { e.stopPropagation(); openLightbox(point.photos, idx); }}
                        />
                      ))}
                      {point.photos.length > 3 && (
                        <div className="w-20 h-20 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)' }}>
                          +{point.photos.length - 3}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePoint(point.id);
                  }}
                  className="p-2 rounded"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Point Modal */}
      {showAddModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }} onClick={() => setShowAddModal(false)}>
          <div className="card-floating p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto anim-scale" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--text)' }}>Add Scouting Point</h3>
            
            {/* Coordinates */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="label-depth">Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={newPoint.lat}
                  onChange={(e) => setNewPoint(prev => ({ ...prev, lat: e.target.value }))}
                  className="input-depth"
                  placeholder="43.2141"
                />
              </div>
              <div>
                <label className="label-depth">Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={newPoint.lng}
                  onChange={(e) => setNewPoint(prev => ({ ...prev, lng: e.target.value }))}
                  className="input-depth"
                  placeholder="27.9147"
                />
              </div>
            </div>
            
            <button
              onClick={() => {
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      setNewPoint(prev => ({
                        ...prev,
                        lat: pos.coords.latitude.toString(),
                        lng: pos.coords.longitude.toString()
                      }));
                      toast.success('Location captured');
                    },
                    () => toast.error('Could not get location')
                  );
                }
              }}
              className="btn-depth-secondary text-sm mb-2 w-full"
            >
              <MapPin className="w-4 h-4 inline mr-1" />
              Use Current Location
            </button>
            
            {onOpenMap && (
              <button
                onClick={() => {
                  setShowAddModal(false);
                  onOpenMap(true); // Open vegetation panel with scout mode
                }}
                className="btn-depth-secondary text-sm mb-4 w-full"
                data-testid="choose-from-map-btn"
              >
                <MapPin className="w-4 h-4 inline mr-1" />
                Choose from Map
              </button>
            )}

            {/* Checklist */}
            <div className="mb-4">
              <label className="label-depth mb-2 block">Issues Observed</label>
              <div className="grid grid-cols-2 gap-2">
                {CHECKLIST_ITEMS.map(item => (
                  <label
                    key={item.key}
                    className="flex items-center gap-2 p-2 rounded-lg cursor-pointer transition"
                    style={{
                      background: newPoint.checklist[item.key] ? 'rgba(198,170,88,0.15)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${newPoint.checklist[item.key] ? 'var(--gold)' : 'var(--border-soft)'}`,
                      color: 'var(--text)'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={newPoint.checklist[item.key]}
                      onChange={(e) => setNewPoint(prev => ({
                        ...prev,
                        checklist: { ...prev.checklist, [item.key]: e.target.checked }
                      }))}
                      className="sr-only"
                    />
                    <span>{item.icon}</span>
                    <span className="text-sm">{item.label}</span>
                    {newPoint.checklist[item.key] && (
                      <Check className="w-4 h-4 ml-auto" style={{ color: 'var(--gold)' }} />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="mb-4">
              <label className="label-depth">Notes</label>
              <textarea
                value={newPoint.notes}
                onChange={(e) => setNewPoint(prev => ({ ...prev, notes: e.target.value }))}
                className="input-depth"
                rows={3}
                placeholder="Describe what you observed..."
              />
            </div>

            {/* Photos */}
            <div className="mb-4">
              <label className="label-depth mb-2 block">Photos</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                id="photo-upload"
              />
              <label
                htmlFor="photo-upload"
                className="flex items-center justify-center gap-2 p-4 rounded-lg cursor-pointer transition"
                style={{ border: '2px dashed var(--border)', color: 'var(--text-muted)' }}
              >
                <Upload className="w-5 h-5" />
                <span>Upload photos</span>
              </label>
              
              {newPoint.photos.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {newPoint.photos.map((photo, index) => (
                    <PhotoThumbnail 
                      key={index} 
                      photo={photo}
                      onRemove={() => removePhoto(index)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => handleCreatePoint()}
                disabled={!newPoint.lat || !newPoint.lng || uploading}
                className="btn-depth-primary flex-1 disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Point'
                )}
              </button>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetNewPoint();
                }}
                className="btn-depth-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Point Detail Modal */}
      {selectedPoint && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }} onClick={() => setSelectedPoint(null)}>
          <div className="card-floating p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto anim-scale" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold" style={{ color: 'var(--text)' }}>Scouting Point Details</h3>
              <button onClick={() => setSelectedPoint(null)} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            
            {/* Status selector */}
            <div className="mb-4">
              <label className="label-depth mb-2 block">Status</label>
              <div className="flex gap-2">
                {STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleUpdateStatus(selectedPoint.id, opt.value)}
                    className="px-3 py-1.5 rounded-lg text-sm transition"
                    style={{
                      background: selectedPoint.status === opt.value 
                        ? (opt.value === 'resolved' ? 'rgba(34,197,94,0.15)' : opt.value === 'checked' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)')
                        : 'rgba(255,255,255,0.06)',
                      color: selectedPoint.status === opt.value
                        ? (opt.value === 'resolved' ? 'var(--success)' : opt.value === 'checked' ? 'var(--info)' : 'var(--warning)')
                        : 'var(--text-secondary)',
                      border: `1px solid ${selectedPoint.status === opt.value ? 'transparent' : 'var(--border-soft)'}`
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Location */}
            <div className="mb-4 text-sm">
              <span style={{ color: 'var(--text-muted)' }}>Location:</span>{' '}
              <span style={{ color: 'var(--text)' }}>{selectedPoint.lat?.toFixed(6)}, {selectedPoint.lng?.toFixed(6)}</span>
            </div>

            {/* Checklist */}
            <div className="mb-4">
              <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>Issues:</p>
              <div className="flex flex-wrap gap-2">
                {CHECKLIST_ITEMS.map(item => (
                  <span
                    key={item.key}
                    className="px-2 py-1 rounded text-sm"
                    style={{
                      background: selectedPoint.checklist?.[item.key] ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.04)',
                      color: selectedPoint.checklist?.[item.key] ? 'var(--warning)' : 'var(--text-muted)',
                      border: `1px solid ${selectedPoint.checklist?.[item.key] ? 'rgba(245,158,11,0.3)' : 'var(--border-soft)'}`
                    }}
                  >
                    {item.icon} {item.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Notes */}
            {selectedPoint.notes && (
              <div className="mb-4">
                <p className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Notes:</p>
                <p style={{ color: 'var(--text)' }}>{selectedPoint.notes}</p>
              </div>
            )}

            {/* Photos - BUG 1 FIX */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Photos:</p>
                <label className="btn-depth-secondary text-xs cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleAddPhotoToExistingPoint(e, selectedPoint.id)}
                    className="hidden"
                  />
                  <Camera className="w-3 h-3 mr-1 inline" />
                  Add Photo
                </label>
              </div>
              
              {selectedPoint.photos?.length > 0 ? (
                <div className="grid grid-cols-4 gap-2">
                  {selectedPoint.photos.map((photo, index) => (
                    <PhotoThumbnail
                      key={index}
                      photo={photo}
                      onClick={() => openLightbox(selectedPoint.photos, index)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm italic" style={{ color: 'var(--text-muted)' }}>No photos yet</p>
              )}
            </div>

            {/* Metadata */}
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Created: {new Date(selectedPoint.createdAt).toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxPhotos && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxPhotos(null)}
        />
      )}
    </div>
  );
};

export default ScoutingPanel;
